import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Calendar, Message, toaster } from 'rsuite';
import styled from 'styled-components';
import useSWR from 'swr';
import axios from 'axios';

import ControlPanel from './ControlPanel';
import CalendarCell from './CalendarCell';
import AddScheduleModal from './AddScheduleModal';

import 'rsuite/Calendar/styles/index.css';
import 'rsuite/Popover/styles/index.css';
import 'rsuite/Modal/styles/index.css';
import 'rsuite/Button/styles/index.css';
import 'rsuite/SelectPicker/styles/index.css';
import 'rsuite/CheckPicker/styles/index.css';

let dataCaches = {};

const WorkScheduleCalendar = () => {
    const { data: departmentRes } = useSWR(`/api/work-schedule/departments`, axios);
    const { data: enabledRes } = useSWR(`/api/work-schedule/enabled`, axios);
    const { data: typesRes } = useSWR(`/api/work-schedule-types`, axios);
    const [nowDate, setNowDate] = useState(new Date());
    const [selectedDepartment, setSelectedDepartment] = useState();
    const [selectedEmployee, setSelectedEmployee] = useState();
    const [loading, setLoading] = useState(false);
    const [nowSchedules, setNowSchedules] = useState({});
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedSchedules, setSelectedSchedules] = useState([]);  // 新增：多個班別選擇
    const [canManage, setCanManage] = useState(false);
    const [draggedItem, setDraggedItem] = useState(null);
    const [modalError, setModalError] = useState(''); // 新增：Modal 錯誤信息
    const [isSelectionMode, setIsSelectionMode] = useState(false); // 新增：選取模式狀態
    const [selectedDates, setSelectedDates] = useState([]); // 新增：選取的日期列表

    const departments = useMemo(() => departmentRes?.data || [], [departmentRes]);
    const isEnabled = useMemo(() => enabledRes?.data?.enabled || false, [enabledRes]);
    const scheduleTypes = useMemo(() => typesRes?.data?.types || [], [typesRes]);
    const typeColors = useMemo(() => typesRes?.data?.colors || {}, [typesRes]);
    const nowMonth = useMemo(() => `${nowDate.getFullYear()}-${nowDate.getMonth() + 1}`, [nowDate]);

    // 根據類型 ID 獲取顏色
    const getColorForType = useCallback((typeId) => {
        const type = scheduleTypes.find(t => parseInt(t.id) === parseInt(typeId));
        return type?.color || '#f0f0f0';
    }, [scheduleTypes]);

    // 根據類型 ID 獲取名稱
    const getNameForType = useCallback((typeId) => {
        const type = scheduleTypes.find(t => parseInt(t.id) === parseInt(typeId));
        return type?.name || '未知班別';
    }, [scheduleTypes]);

    const { data: usersRes } = useSWR(
        selectedDepartment ? `/api/work-schedule/users?department=${selectedDepartment}` : `/api/work-schedule/users`,
        axios
    );
    const users = useMemo(() => usersRes?.data || [], [usersRes]);

    useEffect(() => {
        setLoading(true);
        fetchSchedules(nowDate, selectedDepartment, selectedEmployee).then(data => {
            setNowSchedules(data);
            setLoading(false);
        });
    }, [nowDate, selectedDepartment, selectedEmployee]);

    // 檢查用戶權限
    useEffect(() => {
        // 這裡可以加入權限檢查邏輯
        setCanManage(true);
    }, []);

    function getSearchParamString(date, departmentId, employeeId) {
        const searchParams = new URLSearchParams();
        searchParams.append('year', date.getFullYear());
        searchParams.append('month', date.getMonth() + 1);
        if (departmentId) {
            searchParams.append('department', departmentId);
        }
        if (employeeId) {
            searchParams.append('employee', employeeId);
        }
        return searchParams.toString();
    }

    async function fetchSchedules(date, departmentId, employeeId) {
        const searchParamsString = getSearchParamString(date, departmentId, employeeId);
        let data = undefined;

        if (dataCaches[searchParamsString]) {
            data = dataCaches[searchParamsString];
        } else {
            const res = await axios.get(`/api/work-schedule/schedules?${searchParamsString}`);
            data = res?.data || {};
            dataCaches[searchParamsString] = data;
        }

        return data;
    }

    function handleDepartmentChange(value) {
        setSelectedDepartment(value || undefined);
        setSelectedEmployee(undefined);
    }

    function handleEmployeeChange(value) {
        setSelectedEmployee(value || undefined);
    }

    function formatDate(date) {
        if (!date) return '';
        
        let dateObj;
        if (date instanceof Date) {
            dateObj = date;
        } else if (typeof date === 'string' || typeof date === 'number') {
            dateObj = new Date(date);
        } else {
            return '';
        }
        
        if (isNaN(dateObj.getTime())) {
            return '';
        }
        
        let year = dateObj.getFullYear();
        let month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
        let day = dateObj.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    function handleDateClick(date) {
        if (!canManage) return;
        
        // 如果在選取模式，則切換該日期的選取狀態
        if (isSelectionMode) {
            const dateStr = formatDate(date);
            setSelectedDates(prev => {
                if (prev.includes(dateStr)) {
                    return prev.filter(d => d !== dateStr);
                } else {
                    return [...prev, dateStr];
                }
            });
            return;
        }
    }

    function handleDateDoubleClick(date) {
        if (!canManage) return;
        
        // 正常模式：開啟新增班表 Modal
        setSelectedDate(date);
        setSelectedUsers([]);
        setSelectedSchedules([]);
        setModalError(''); // 清除錯誤信息
        setShowAddModal(true);
    }

    function handleRemoveSchedule(scheduleId, date) {
        if (!canManage) return;
        
        axios.delete('/api/work-schedule/destroy', {
            data: { schedule_id: scheduleId }
        }).then(() => {
            dataCaches = {};
            fetchSchedules(nowDate, selectedDepartment, selectedEmployee).then(data => {
                setNowSchedules(data);
            });
            toaster.push(<Message type="success">班表移除成功</Message>, { placement: 'topCenter' });
        }).catch(err => {
            console.error('移除班表失敗:', err);
            const responseData = err.response?.data;
            const errorMessage = responseData?.message || '移除班表失敗';
            toaster.push(<Message type="error">{errorMessage}</Message>, { placement: 'topCenter' });
        });
    }

    // 處理在 Modal 中移除已排班的功能
    function handleRemoveExistingSchedule(scheduleId) {
        if (!canManage) return;
        
        axios.delete('/api/work-schedule/destroy', {
            data: { schedule_id: scheduleId }
        }).then(() => {
            dataCaches = {};
            fetchSchedules(nowDate, selectedDepartment, selectedEmployee).then(data => {
                setNowSchedules(data);
            });
            toaster.push(<Message type="success">班表移除成功</Message>, { placement: 'topCenter' });
        }).catch(err => {
            console.error('移除班表失敗:', err);
            const responseData = err.response?.data;
            const errorMessage = responseData?.message || '移除班表失敗';
            toaster.push(<Message type="error">{errorMessage}</Message>, { placement: 'topCenter' });
        });
    }

    // 清除錯誤信息（當用戶修改選擇時）
    function clearModalError() {
        if (modalError) {
            setModalError('');
        }
    }

    // 進入選取模式
    function enterSelectionMode() {
        setIsSelectionMode(true);
        setSelectedDates([]);
    }

    // 退出選取模式
    function exitSelectionMode() {
        setIsSelectionMode(false);
        setSelectedDates([]);
    }

    // 批次新增班表
    function handleBatchAddSchedule() {
        if (selectedDates.length === 0) return;
        
        setSelectedDate(null); // 清空單一日期選擇
        setSelectedUsers([]);
        setSelectedSchedules([]);
        setModalError('');
        setShowAddModal(true);
    }

    // 新增班別到選擇列表
    function addScheduleType() {
        const newSchedule = {
            id: Date.now(), // 暫時 ID，用於 React key
            type_id: scheduleTypes[0]?.id ? parseInt(scheduleTypes[0].id) : ''
            // start_time 和 end_time 不需要存在這裡，直接從 scheduleTypes 取得用於顯示
        };
        
        clearModalError(); // 清除錯誤信息
        setSelectedSchedules([...selectedSchedules, newSchedule]);
    }

    // 移除班別
    function removeScheduleType(id) {
        clearModalError(); // 清除錯誤信息
        setSelectedSchedules(selectedSchedules.filter(s => s.id !== id));
    }

    // 更新班別資料
    function updateScheduleType(id, field, value) {
        clearModalError(); // 清除錯誤信息
        setSelectedSchedules(selectedSchedules.map(s => 
            s.id === id ? { ...s, [field]: value } : s
        ));
    }

    // 檢查時間重疊
    async function checkTimeOverlap(userUuid, date, schedules) {
        try {
            // 先將 schedules 轉換為包含實際時間的格式
            const schedulesWithTime = schedules.map(schedule => {
                const selectedType = scheduleTypes.find(t => parseInt(t.id) === schedule.type_id);
                return {
                    ...schedule,
                    start_time: selectedType?.start_time || '',
                    end_time: selectedType?.end_time || ''
                };
            });

            for (let schedule of schedulesWithTime) {
                const response = await axios.post('/api/work-schedule/check-overlap', {
                    user_uuid: userUuid,
                    date: date,
                    type_id: schedule.type_id
                });

                if (response.data.has_overlap) {
                    return {
                        hasOverlap: true,
                        message: `班別時間 ${schedule.start_time}-${schedule.end_time} 與現有班別重疊`,
                        overlappingSchedules: response.data.overlapping_schedules
                    };
                }
            }

            // 檢查新增班別之間是否重疊
            for (let i = 0; i < schedulesWithTime.length; i++) {
                for (let j = i + 1; j < schedulesWithTime.length; j++) {
                    if (timeRangesOverlap(schedulesWithTime[i], schedulesWithTime[j])) {
                        return {
                            hasOverlap: true,
                            message: `班別時間重疊：${schedulesWithTime[i].start_time}-${schedulesWithTime[i].end_time} 與 ${schedulesWithTime[j].start_time}-${schedulesWithTime[j].end_time}`
                        };
                    }
                }
            }

            return { hasOverlap: false };
        } catch (error) {
            console.error('檢查時間重疊失敗:', error);
            // 當 API 調用失敗時，返回錯誤信息而不是直接忽略
            return { 
                hasOverlap: true, 
                message: `檢查時間重疊時發生錯誤：${error.response?.data?.message || error.message || '未知錯誤'}`,
                isApiError: true 
            };
        }
    }

    // 檢查兩個時間範圍是否重疊
    function timeRangesOverlap(schedule1, schedule2) {
        const start1 = schedule1.start_time;
        const end1 = schedule1.end_time;
        const start2 = schedule2.start_time;
        const end2 = schedule2.end_time;

        // 處理跨日班別
        const isSchedule1CrossDay = start1 > end1;
        const isSchedule2CrossDay = start2 > end2;

        if (!isSchedule1CrossDay && !isSchedule2CrossDay) {
            // 兩個都不跨日
            return start1 < end2 && start2 < end1;
        } else if (isSchedule1CrossDay && !isSchedule2CrossDay) {
            // 只有第一個跨日
            return start1 < end2 || start2 < end1;
        } else if (!isSchedule1CrossDay && isSchedule2CrossDay) {
            // 只有第二個跨日
            return start2 < end1 || start1 < end2;
        } else {
            // 兩個都跨日
            return true; // 跨日班別必然重疊
        }
    }

    // 檢查法規合規性
    async function checkCompliance(userUuid, dateStr, typeId) {
        try {
            const response = await axios.post('/api/work-schedule/validate-compliance', {
                user_uuid: userUuid,
                date: dateStr,
                type_id: typeId
            });
            return response.data;
        } catch (error) {
            console.error('檢查法規合規性失敗:', error);
            return { is_valid: true, warnings: [] };
        }
    }

    async function handleAddSchedule() {
        // 清除之前的錯誤信息
        setModalError('');

        // 決定要處理的日期列表
        const datesToProcess = selectedDates.length > 0 ? selectedDates : [formatDate(selectedDate)];

        if (datesToProcess.length === 0) {
            setModalError('請選擇日期');
            return;
        }

        if (selectedUsers.length === 0) {
            setModalError('請選擇員工');
            return;
        }

        if (selectedSchedules.length === 0) {
            setModalError('請至少新增一個班別');
            return;
        }

        // 驗證班別資料
        for (let schedule of selectedSchedules) {
            if (!schedule.type_id) {
                setModalError('請選擇班別類型');
                return;
            }
            
            // 從 scheduleTypes 中取得對應的時間資料進行驗證
            const selectedType = scheduleTypes.find(t => parseInt(t.id) === schedule.type_id);
            if (!selectedType || !selectedType.start_time || !selectedType.end_time) {
                setModalError('所選班別缺少時間設定');
                return;
            }
            
            if (selectedType.start_time === selectedType.end_time) {
                setModalError('班別的上班時間和下班時間不能相同');
                return;
            }
        }

        // 檢查每個用戶在每個日期的時間重疊
        for (let userUuid of selectedUsers) {
            for (let dateStr of datesToProcess) {
                const overlapResult = await checkTimeOverlap(userUuid, dateStr, selectedSchedules);
                if (overlapResult.hasOverlap) {
                    const user = users.find(u => u.uuid === userUuid);
                    setModalError(`${user?.name || '用戶'} (${dateStr}): ${overlapResult.message}`);
                    return;
                }
            }
        }

        // 檢查法規合規性並顯示警告
        const complianceWarnings = [];
        for (let userUuid of selectedUsers) {
            for (let dateStr of datesToProcess) {
                for (let schedule of selectedSchedules) {
                    const complianceResult = await checkCompliance(userUuid, dateStr, schedule.type_id);
                    if (!complianceResult.is_valid && complianceResult.warnings.length > 0) {
                        const user = users.find(u => u.uuid === userUuid);
                        complianceWarnings.push(`${user?.name || '用戶'} (${dateStr}): ${complianceResult.warnings.join('；')}`);
                    }
                }
            }
        }

        // 如果有法規警告，顯示確認對話框
        if (complianceWarnings.length > 0) {
            const warningMessage = complianceWarnings.join('\n');
            const confirmed = window.confirm(
                `⚠️ 法規提醒：\n\n${warningMessage}\n\n這些排班可能違反勞基法規定，是否仍要繼續新增？`
            );
            if (!confirmed) {
                return;
            }
        }

        try {
            setLoading(true);
            
            // 批次處理所有日期
            const promises = datesToProcess.map(dateStr => 
                axios.post('/api/work-schedule/store', {
                    user_uuids: selectedUsers,
                    date: dateStr,
                    schedules: selectedSchedules.map(s => {
                        const selectedType = scheduleTypes.find(t => parseInt(t.id) === s.type_id);
                        return {
                            type_id: s.type_id,
                            start_time: selectedType?.start_time || '',
                            end_time: selectedType?.end_time || ''
                        };
                    })
                })
            );

            await Promise.all(promises);

            setShowAddModal(false);
            setSelectedUsers([]);
            setSelectedSchedules([]);
            setSelectedDates([]);
            setIsSelectionMode(false);
            dataCaches = {};
            
            const data = await fetchSchedules(nowDate, selectedDepartment, selectedEmployee);
            setNowSchedules(data);
            
            const successMessage = datesToProcess.length > 1 
                ? `班表批次新增成功 (${datesToProcess.length} 個日期)`
                : '班表新增成功';
            toaster.push(<Message type="success">{successMessage}</Message>, { placement: 'topCenter' });
        } catch (err) {
            console.error('新增班表失敗:', err);
            const responseData = err.response?.data;
            
            if (responseData?.errors && Array.isArray(responseData.errors)) {
                // 顯示具體的錯誤訊息
                responseData.errors.forEach(error => {
                    toaster.push(<Message type="error">{error}</Message>, { placement: 'topCenter' });
                });
            } else {
                const errorMessage = responseData?.message || '新增班表失敗';
                toaster.push(<Message type="error">{errorMessage}</Message>, { placement: 'topCenter' });
            }
        } finally {
            setLoading(false);
        }
    }

    // 拖拉功能處理方法（保持現有邏輯，但需要更新 API 調用）
    function handleDragStart(e, item, fromDate) {
        if (!canManage) return;
        
        const dragData = {
            item: item,
            fromDate: fromDate,
            isShiftKey: e.shiftKey
        };
        
        setDraggedItem(dragData);
        e.dataTransfer.setData('text/plain', JSON.stringify(dragData));
        e.dataTransfer.effectAllowed = e.shiftKey ? 'copy' : 'move';
    }

    function handleDragOver(e) {
        e.preventDefault();
        e.dataTransfer.dropEffect = e.shiftKey ? 'copy' : 'move';
    }

    async function handleDrop(e, toDate) {
        e.preventDefault();
        
        if (!canManage) return;
        
        // 從 dataTransfer 取得拖曳資料，因為狀態更新是非同步的
        let currentDragData = null;
        try {
            const dragDataString = e.dataTransfer.getData('text/plain');
            if (dragDataString) {
                currentDragData = JSON.parse(dragDataString);
            }
        } catch (err) {
            // 忽略解析錯誤
        }
        
        // 優先使用 currentDragData，如果沒有則使用 draggedItem
        const dragData = currentDragData || draggedItem;
        
        if (!dragData || !toDate) return;
        
        const { item, fromDate, isShiftKey } = dragData;
        const toDateStr = formatDate(toDate);
        const fromDateStr = formatDate(fromDate);
        
        if (toDateStr === fromDateStr) {
            setDraggedItem(null);
            return;
        }

        // 檢查法規合規性
        const complianceResult = await checkCompliance(item.user_uuid, toDateStr, item.type_id);
        if (!complianceResult.is_valid && complianceResult.warnings.length > 0) {
            const user = users.find(u => u.uuid === item.user_uuid);
            const warningMessage = complianceResult.warnings.join('；');
            const confirmed = window.confirm(
                `⚠️ 法規提醒：\n\n${user?.name || '用戶'} (${toDateStr}): ${warningMessage}\n\n這個排班可能違反勞基法規定，是否仍要繼續${isShiftKey ? '複製' : '移動'}？`
            );
            if (!confirmed) {
                setDraggedItem(null);
                return;
            }
        }
        
        if (isShiftKey) {
            // 複製模式：保持現有邏輯但使用新的 API 格式
            axios.post('/api/work-schedule/store', {
                user_uuids: [item.user_uuid],
                date: toDateStr,
                schedules: [{
                    type_id: item.type_id,
                    start_time: item.start_time,
                    end_time: item.end_time
                }]
            }).then(() => {
                dataCaches = {};
                fetchSchedules(nowDate, selectedDepartment, selectedEmployee).then(data => {
                    setNowSchedules(data);
                });
                toaster.push(<Message type="success">班表複製成功</Message>, { placement: 'topCenter' });
            }).catch(err => {
                console.error('複製班表失敗:', err);
                const responseData = err.response?.data;
                const errorMessage = responseData?.message || '複製班表失敗';
                toaster.push(<Message type="error">{errorMessage}</Message>, { placement: 'topCenter' });
            }).finally(() => {
                setDraggedItem(null);
            });
        } else {
            // 移動模式
            axios.delete('/api/work-schedule/destroy', {
                data: { schedule_id: item.id }
            }).then(() => {
                return axios.post('/api/work-schedule/store', {
                    user_uuids: [item.user_uuid],
                    date: toDateStr,
                    schedules: [{
                        type_id: item.type_id,
                        start_time: item.start_time,
                        end_time: item.end_time
                    }]
                });
            }).then(() => {
                dataCaches = {};
                fetchSchedules(nowDate, selectedDepartment, selectedEmployee).then(data => {
                    setNowSchedules(data);
                });
                toaster.push(<Message type="success">班表移動成功</Message>, { placement: 'topCenter' });
            }).catch(err => {
                console.error('移動班表失敗:', err);
                const responseData = err.response?.data;
                const errorMessage = responseData?.message || '移動班表失敗';
                toaster.push(<Message type="error">{errorMessage}</Message>, { placement: 'topCenter' });
            }).finally(() => {
                setDraggedItem(null);
            });
        }
    }

    const renderCell = useCallback(date => {
        const dateStr = formatDate(date);
        const schedules = nowSchedules[dateStr] || [];
        const isSelected = selectedDates.includes(dateStr);

        return (
            <CalendarCell
                date={date}
                schedules={schedules}
                canManage={canManage}
                getColorForType={getColorForType}
                onDateClick={handleDateClick}
                onDateDoubleClick={handleDateDoubleClick}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragStart={handleDragStart}
                onRemoveSchedule={handleRemoveSchedule}
                cellItemCount={4}
                isSelectionMode={isSelectionMode}
                isSelected={isSelected}
            />
        );
    }, [nowSchedules, canManage, getColorForType, isSelectionMode, selectedDates]);

    if (!isEnabled) {
        return (
            <div className="alert alert-info">
                <h4>班表系統尚未啟用</h4>
                <p>請聯絡系統管理員在「設定系統參數」中啟用班表系統。</p>
            </div>
        );
    }

    return (
        <CalendarWrapper>
            <ControlPanel
                departments={departments}
                users={users}
                selectedDepartment={selectedDepartment}
                selectedEmployee={selectedEmployee}
                onDepartmentChange={handleDepartmentChange}
                onEmployeeChange={handleEmployeeChange}
                isSelectionMode={isSelectionMode}
                selectedDatesCount={selectedDates.length}
                onEnterSelectionMode={enterSelectionMode}
                onExitSelectionMode={exitSelectionMode}
                onBatchAddSchedule={handleBatchAddSchedule}
                canManage={canManage}
            />

            <StyledCalendar
                bordered
                weekStart={1}
                value={nowDate}
                onChange={setNowDate}
                renderCell={renderCell}
            />

                                    <AddScheduleModal
                open={showAddModal}
                onClose={() => {
                        setShowAddModal(false);
                        setSelectedUsers([]);
                        setSelectedSchedules([]);
                        setModalError(''); // 清除錯誤信息
                        // 如果是批次模式，退出選取模式
                        if (selectedDates.length > 0) {
                            setIsSelectionMode(false);
                            setSelectedDates([]);
                        }
                }}
                selectedDate={selectedDate}
                selectedDates={selectedDates}
                users={users}
                selectedUsers={selectedUsers}
                onSelectedUsersChange={(users) => {
                    clearModalError(); // 清除錯誤信息
                    setSelectedUsers(users);
                }}
                selectedSchedules={selectedSchedules}
                scheduleTypes={scheduleTypes}
                onAddScheduleType={addScheduleType}
                onRemoveScheduleType={removeScheduleType}
                onUpdateScheduleType={updateScheduleType}
                onConfirm={handleAddSchedule}
                loading={loading}
                existingSchedules={selectedDates.length > 0 ? {} : (nowSchedules[formatDate(selectedDate)] || [])}
                getColorForType={getColorForType}
                formatDate={formatDate}
                errorMessage={modalError}
                canManage={canManage} // 新增：權限管理
                onRemoveExistingSchedule={handleRemoveExistingSchedule} // 新增：刪除已排班
            />

            {loading && (
                <div className="loading-overlay">
                    <div className="loading-spinner">載入中...</div>
                </div>
            )}
        </CalendarWrapper>
    );
};

const CalendarWrapper = styled.div`
    background-color: white;
    border-radius: 8px;
    position: relative;
    
    .work-schedule-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        border-bottom: 1px solid #e5e5e5;
        
        .title {
            font-size: 20px;
            font-weight: bold;
        }
        
        .controls {
            display: flex;
            align-items: center;
            gap: 10px;
        }
    }
    
    .calendar-schedule-list {
        list-style: none;
        padding: 0;
        margin: 0;
        
        li {
            font-size: 12px;
            padding: 1px 0;
            
            button {
                opacity: 0.5;
                &:hover {
                    opacity: 1;
                }
            }
        }
    }
    
    .calendar-cell-container {
        &:hover {
            background-color: rgba(0, 123, 255, 0.05);
        }
        
        &.drag-over {
            background-color: rgba(0, 123, 255, 0.1);
            border: 2px dashed #007bff;
        }
        
        &.selected {
            background-color: #e3f2fd !important;
            border: 2px solid #2196f3 !important;
            border-radius: 4px;
        }
    }
    
    .calendar-schedule-list li span[draggable="true"] {
        &:hover {
            background-color: rgba(0, 123, 255, 0.1);
            border-radius: 3px;
            padding: 1px 3px;
        }
        
        &:active {
            opacity: 0.7;
        }
    }
    
    .loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(255, 255, 255, 0.8);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
        
        .loading-spinner {
            padding: 20px;
            background-color: white;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }
    }
`

const StyledCalendar = styled(Calendar)`
    .rs-calendar-table-cell-content {
        min-height: 160px;
    }
`

export default WorkScheduleCalendar